import {
  isInternalNoteType,
  isLogType,
  isMasterTechnicalLogMessage,
  isPartnerApiLogMessage,
  isAiAgentLogMessage,
  isLeadAccessLog,
  isMessageFromLoggedInUser,
  getMessageAuthorUserId,
} from "./messageTypeRegistry";
import { resolveMessageViaChannelLabel } from "../../utils/messageInteractionChannelLabel";
import { formatPartnerApiUserFacingSummary } from "../../utils/partnerApiAuditDisplay";

const LOG_GROUP_WINDOW_MS = 2 * 60 * 1000;

export const getMessageDate = (message) => {
  if (!message) return null;

  // Prefer insert time (createdAt) over delivery-updated lastUpdate so status
  // ACKs and master audit rows without data_ultima_alteracao keep timeline order.
  let raw =
    message.createdAt ||
    message.timestamp ||
    message.updatedAt ||
    message.lastUpdate;

  if (raw == null && message.ts != null) {
    const n = Number(message.ts);
    raw = Number.isFinite(n) ? new Date(n) : null;
  }

  if (!raw) return null;

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    const mysqlRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    if (mysqlRegex.test(trimmed)) {
      const d = new Date(`${trimmed.replace(" ", "T")}-03:00`);
      return isNaN(d.getTime()) ? null : d;
    }
  }

  if (raw instanceof Date) {
    return isNaN(raw.getTime()) ? null : raw;
  }

  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

export const sortMessagesChronologically = (messages) => {
  const list = Array.isArray(messages) ? [...messages] : [];
  const idOf = (m) => (typeof m?.id === "number" ? m.id : Number(m?.id) || 0);

  // Prefer createdAt (stable insert time) over lastUpdate so delivery ACKs and
  // master audit rows stay interleaved with channel messages. Fall back to id
  // (same key as beforeMessageId pagination). Never push null-date items first —
  // that clustered logs above messages for master users.
  list.sort((a, b) => {
    const da = getMessageDate(a);
    const db = getMessageDate(b);
    if (da && db) {
      const byDate = da.getTime() - db.getTime();
      if (byDate !== 0) return byDate;
    }
    return idOf(a) - idOf(b);
  });

  return list;
};

const PLATFORM_TZ = "America/Araguaina";

const zonedYmd = (date) => {
  if (!date) return "";
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: PLATFORM_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  } catch {
    return "";
  }
};

const isSameDay = (a, b) => zonedYmd(a) !== "" && zonedYmd(a) === zonedYmd(b);

export const formatDateSeparatorLabel = (date) => {
  if (!date) return "";
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  if (isSameDay(date, now)) return "Hoje";
  if (isSameDay(date, yesterday)) return "Ontem";

  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: PLATFORM_TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

export const getLogSenderId = (message) => {
  const metaId = message?.logMeta?.actorUserId;
  if (metaId != null && metaId !== "" && Number(metaId) !== 0) {
    return String(metaId);
  }
  if (message?.senderId != null && message?.senderId !== "" && Number(message.senderId) !== 0) {
    return String(message.senderId);
  }
  if (message?.id_usuario != null && message?.id_usuario !== "" && Number(message.id_usuario) !== 0) {
    return String(message.id_usuario);
  }
  return "";
};

/** Nome do executor de um log de atividade (usuário, chatbot, automação ou sistema). */
export const getLogActorDisplayName = (message, loggedUser) => {
  if (!message) return "";

  const kind = message?.logMeta?.actorKind;
  const actorId = getLogSenderId(message);
  const rawName = message?.logMeta?.actorUserName || message?.senderName || message?.userName || "";

  if (kind === "automation") {
    return rawName || "Automação";
  }
  if (kind === "chatbot") {
    return rawName || "Chatbot";
  }
  if (kind === "ai_agent") {
    return rawName || "Agente de IA";
  }
  if (kind === "system" || (!actorId && !rawName)) {
    return rawName || "Sistema";
  }

  if (
    loggedUser?.id != null &&
    actorId &&
    String(loggedUser.id) === String(actorId)
  ) {
    return "Você";
  }

  if (rawName && !["Sistema", "Cliente"].includes(rawName)) {
    return rawName;
  }

  if (message?.senderType === "chatbot") return "Chatbot";
  if (actorId) return "Atendente";
  return rawName || "Sistema";
};

const PLACEHOLDER_ACTOR_NAMES = new Set([
  "você",
  "voce",
  "sistema",
  "atendente",
  "cliente",
  "chatbot",
  "automação",
  "automacao",
]);

function voiceActorNameFromMessage(message) {
  const raw = message?.json_whatsapp;
  if (!raw) return "";
  try {
    const json = typeof raw === "string" ? JSON.parse(raw) : raw;
    const name =
      json?.xpro_voice_call?.actorUserName || json?.xpro_log?.actorUserName || "";
    return String(name || "").trim();
  } catch {
    return "";
  }
}

/** Nome real de quem executou a nota, sem trocar pelo "Você" do visualizador. */
export const getSharedNoteActorName = (message) => {
  const raw = String(
    message?.logMeta?.actorUserName ||
      message?.senderName ||
      message?.userName ||
      voiceActorNameFromMessage(message) ||
      ""
  ).trim();
  if (!raw || PLACEHOLDER_ACTOR_NAMES.has(raw.toLowerCase())) return "";
  return raw;
};

const viewerIsNoteActor = (message, loggedUser) => {
  const actorId = getLogSenderId(message);
  if (!actorId || loggedUser?.id == null || loggedUser.id === "") return false;
  return String(loggedUser.id) === String(actorId);
};

/**
 * Notas de telefonia são compartilhadas. "Você" e "Atendente" só fazem sentido
 * para quem executou a ação; os demais veem o nome de quem atendeu.
 */
export const personalizeSharedAttendantNote = (text, message, loggedUser) => {
  const plain = String(text || "");
  const name = getSharedNoteActorName(message);
  if (!name || !plain) return plain;
  let next = plain;
  if (!viewerIsNoteActor(message, loggedUser)) {
    next = next
      .replace(/^Você atendeu da fila/i, `${name} atendeu da fila`)
      .replace(/^Você atendeu a ligação/i, `${name} atendeu a ligação`)
      .replace(/^Você entrou na ligação/i, `${name} entrou na ligação`)
      .replace(/^Você ligou/i, `${name} ligou`)
      .replace(/Ligação transferida para você/gi, `Ligação transferida para ${name}`)
      .replace(/Você puxou da fila de espera/gi, `${name} puxou da fila de espera`);
  }
  return next
    .replace(/Atendente aceitou da fila/gi, `${name} aceitou da fila`)
    .replace(/Atendente atendeu da fila/gi, `${name} atendeu da fila`)
    .replace(/Sessão do atendente ativa/gi, `Sessão de ${name} ativa`)
    .replace(/Fila ofereceu ao próximo livre/gi, `Fila ofereceu a ${name}`);
};

const stripLogHtml = (html) =>
  typeof html === "string" ? html.replace(/<[^>]*>/g, "").trim() : "";

/** Resumo curto de um log de atividade para a timeline colapsada. */
export const getActivityLogSummary = (message, loggedUser) => {
  const stored = String(message?.logMeta?.summary || "").trim();
  if (stored) {
    const rewritten = personalizeSharedAttendantNote(stored, message, loggedUser);
    if (rewritten.length > 120) return `${rewritten.slice(0, 117)}…`;
    return rewritten;
  }
  if (message?.logMeta?.code === "automation_rule") {
    const name = message.logMeta?.ruleName;
    const actions = message.logMeta?.actionsSummary;
    if (name && Array.isArray(actions) && actions.length) {
      const preview = actions.slice(0, 2).join("; ");
      const extra = actions.length > 2 ? ` (+${actions.length - 2})` : "";
      return `Automação «${name}»: ${preview}${extra}`;
    }
    if (name) return `Automação «${name}»`;
  }
  if (message?.logMeta?.code === "transfer_user") {
    const storedSummary = String(message.logMeta?.summary || "").trim();
    if (storedSummary) {
      return storedSummary.length > 140 ? `${storedSummary.slice(0, 137)}…` : storedSummary;
    }
    const from = message.logMeta?.from?.label;
    const to = message.logMeta?.to?.label;
    const motivo = String(message.logMeta?.detail?.reasoning || "").trim();
    if (from && to && motivo) return `Transferida: ${from} → ${to} · ${motivo}`;
    if (from && to) return `Transferida: ${from} → ${to}`;
  }
  if (message?.logMeta?.code === "survey_response" && message.logMeta?.surveyTitle) {
    return `Pesquisa respondida: ${message.logMeta.surveyTitle}`;
  }
  if (message?.logMeta?.code === "lead_view") {
    return "Visualizou esta solicitação";
  }
  if (isPartnerApiLogMessage(message)) {
    const friendly = formatPartnerApiUserFacingSummary(message);
    if (friendly) {
      return friendly.length > 100 ? `${friendly.slice(0, 97)}…` : friendly;
    }
  }
  const plain = (message?.textPlain || stripLogHtml(message?.text || ""))
    .split(/\s+(?:Confiança|Motivo)\s*:/i)[0]
    .trim();
  const rewritten = formatPartnerApiUserFacingSummary(plain);
  const display = personalizeSharedAttendantNote(rewritten || plain, message, loggedUser);
  if (display.length > 100) return `${display.slice(0, 97)}…`;
  return display;
};

export const getLogBatchKey = (message) => {
  const batchId = message?.logMeta?.batchId;
  if (batchId) return `batch:${batchId}`;
  return null;
};

const getLogGroupKind = (message) => {
  if (isPartnerApiLogMessage(message)) return "api";
  if (isAiAgentLogMessage(message)) return "ai";
  if (isMasterTechnicalLogMessage(message)) return "technical";
  return "activity";
};

export const shouldGroupLogs = (prev, next) => {
  if (!prev || !next || !isLogType(prev) || !isLogType(next)) return false;
  if (isLeadAccessLog(prev) || isLeadAccessLog(next)) return false;

  const videoCodes = new Set([
    "video_call_summary",
    "video_call_transcript",
    "video_recording_ready",
  ]);
  const voiceCodes = new Set(["voice_call", "voice_call_transcript"]);
  const prevCode = prev?.logMeta?.code;
  const nextCode = next?.logMeta?.code;
  if (videoCodes.has(prevCode) || videoCodes.has(nextCode)) return false;
  if (voiceCodes.has(prevCode) || voiceCodes.has(nextCode)) return false;
  try {
    const prevJson =
      typeof prev?.json_whatsapp === "string"
        ? JSON.parse(prev.json_whatsapp || "{}")
        : prev?.json_whatsapp;
    const nextJson =
      typeof next?.json_whatsapp === "string"
        ? JSON.parse(next.json_whatsapp || "{}")
        : next?.json_whatsapp;
    if (prevJson?.xpro_video_call_summary || nextJson?.xpro_video_call_summary) return false;
    if (prevJson?.xpro_voice_call || nextJson?.xpro_voice_call) return false;
    if (prevJson?.xpro_voice_call_transcript || nextJson?.xpro_voice_call_transcript) return false;
  } catch {
    /* ignore */
  }

  const prevKind = getLogGroupKind(prev);
  const nextKind = getLogGroupKind(next);
  // API/IA audits must stay as individual ActivityLogMessage rows so the
  // request/response/webhook detail panels remain available.
  if (prevKind === "api" || prevKind === "ai" || prevKind === "technical") return false;
  if (nextKind === "api" || nextKind === "ai" || nextKind === "technical") return false;
  if (prevKind !== nextKind) return false;

  const prevBatch = getLogBatchKey(prev);
  const nextBatch = getLogBatchKey(next);
  if (prevBatch && nextBatch && prevBatch === nextBatch) return true;

  const prevDate = getMessageDate(prev);
  const nextDate = getMessageDate(next);
  if (!prevDate || !nextDate) return false;

  const sameSender = getLogSenderId(prev) === getLogSenderId(next);
  const withinWindow = Math.abs(nextDate - prevDate) <= LOG_GROUP_WINDOW_MS;
  return sameSender && withinWindow;
};

/** Chave estável do autor de anotação interna (para agrupar sequências consecutivas). */
export const getInternalNoteSenderKey = (message) => {
  const userId = getMessageAuthorUserId(message);
  if (userId) return `uid:${userId}`;
  const name = (message?.senderName || message?.userName || "").trim().toLowerCase();
  return name ? `name:${name}` : "unknown";
};

/** Anotações internas consecutivas do mesmo autor e mesmo lado (própria vs. equipe). */
export const shouldClusterInternalNotes = (prev, next, loggedUser) => {
  if (!isInternalNoteType(prev) || !isInternalNoteType(next)) return false;
  // Anotação excluída vira uma linha compacta, fora do balão agrupado.
  if (prev?.deletedAt || next?.deletedAt) return false;
  if (getInternalNoteSenderKey(prev) !== getInternalNoteSenderKey(next)) return false;
  const ownPrev = isMessageFromLoggedInUser(prev, loggedUser);
  const ownNext = isMessageFromLoggedInUser(next, loggedUser);
  return ownPrev === ownNext;
};

export const INTERNAL_NOTE_CLUSTER_DEFAULT = {
  isGrouped: false,
  isFirst: true,
  isLast: true,
  isOnly: true,
};

export const getInternalNoteCluster = (messages, index, loggedUser) => {
  const list = Array.isArray(messages) ? messages : [];
  const msg = list[index];
  if (!msg || !isInternalNoteType(msg)) return INTERNAL_NOTE_CLUSTER_DEFAULT;

  const withPrev =
    index > 0 && shouldClusterInternalNotes(list[index - 1], msg, loggedUser);
  const withNext =
    index < list.length - 1 && shouldClusterInternalNotes(msg, list[index + 1], loggedUser);
  const isGrouped = withPrev || withNext;

  return {
    isGrouped,
    isFirst: !withPrev,
    isLast: !withNext,
    isOnly: !withPrev && !withNext,
  };
};

export const groupConsecutiveLogs = (messages) => {
  const items = [];
  let i = 0;

  while (i < messages.length) {
    const msg = messages[i];
    if (!isLogType(msg)) {
      items.push({ kind: "message", message: msg });
      i += 1;
      continue;
    }

    const group = [msg];
    let j = i + 1;
    while (j < messages.length && shouldGroupLogs(group[group.length - 1], messages[j])) {
      group.push(messages[j]);
      j += 1;
    }

    if (group.length === 1) {
      items.push({ kind: "log", message: msg });
    } else {
      items.push({ kind: "logGroup", messages: group, id: `log-group-${group.map((m) => m.id).join("-")}` });
    }
    i = j;
  }

  return items;
};

export const getMessageChannelKey = (message, conversation = {}) => {
  const label = resolveMessageViaChannelLabel(message, conversation);
  const raw = (label || message?.channelName || message?.channel || "").toString().trim().toLowerCase();
  return raw || "unknown";
};

export const buildTimelineItems = (messages, conversation = {}) => {
  const ordered = sortMessagesChronologically(messages);
  const grouped = groupConsecutiveLogs(ordered);
  const timeline = [];
  let lastDayKey = null;
  let lastChannelKey = null;

  grouped.forEach((item) => {
    const refMessage = item.kind === "logGroup" ? item.messages[0] : item.message;
    const date = getMessageDate(refMessage);
    const dayKey = date ? zonedYmd(date) : null;

    if (dayKey && dayKey !== lastDayKey) {
      timeline.push({
        kind: "dateSeparator",
        id: `date-${dayKey}`,
        label: formatDateSeparatorLabel(date),
        date,
      });
      lastDayKey = dayKey;
      lastChannelKey = null;
    }

    if (item.kind === "message" && !isLogType(item.message)) {
      const channelKey = getMessageChannelKey(item.message, conversation);
      if (lastChannelKey && channelKey !== lastChannelKey && channelKey !== "unknown") {
        const channelLabel =
          resolveMessageViaChannelLabel(item.message, conversation) ||
          item.message?.channelName ||
          item.message?.channel ||
          channelKey;
        timeline.push({
          kind: "channelMarker",
          id: `channel-${item.message.id}-${channelKey}`,
          channelLabel,
        });
      }
      if (channelKey !== "unknown") lastChannelKey = channelKey;
    }

    timeline.push(item);
  });

  return timeline;
};

export const ACTIVITY_EXPAND_PREF_KEY = "xpro.chat.activityLogsExpanded";

export const getActivityExpandPreference = () => {
  try {
    return localStorage.getItem(ACTIVITY_EXPAND_PREF_KEY) === "true";
  } catch {
    return false;
  }
};

export const setActivityExpandPreference = (expanded) => {
  try {
    localStorage.setItem(ACTIVITY_EXPAND_PREF_KEY, expanded ? "true" : "false");
  } catch {
    /* ignore */
  }
};
